import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { NestedTreeControl } from '@angular/cdk/tree';
import { DOCUMENT } from '@angular/common';
import { Component, inject, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import { ActivatedRoute, Router } from '@angular/router';
import { decompress } from 'lz-string';
import { BehaviorSubject, combineLatest, fromEvent, map, merge, Observable, of, shareReplay, startWith, Subject, Subscription, takeUntil, withLatestFrom } from 'rxjs';
import { ClientGoals, ClientGoalsDisplay, GroupGoals, GroupGoalsDisplay, GroupGoalsWithQuantity } from 'src/app/Interfaces/goals';
import { ToNormalNumberPipe } from 'src/app/Pipes/to-normal-number.pipe';
import { StoreService } from 'src/app/services/store.service';
import { ExcelObject, UtilService } from 'src/app/services/util.service';
import { monthsHeb } from 'src/app/Utility/constants';
import { FormattedLog } from 'src/app/Utility/FormattedConsole';
import { groupByKeyOfData, normalizeNumber } from 'src/app/Utility/functions';
import { environment } from 'src/environments/environment';
import { utils, writeFile } from 'xlsx';

const toNormalNumber = ToNormalNumberPipe.prototype.transform;

const goalsValues = ['currentYearGoal', 'total', 'advancementPercentage', 'lastYearTotal', 'growthPercentage'] as const;
const agentsValues = ['AgentCode', 'AgentName', ...goalsValues] as const;
const clientsValues = ['Cl', 'currentYearGoal', ...goalsValues] as const;
const groupsValues = ['GroupId', 'GroupName', 'AgentId', 'AgentName', ...goalsValues] as const;

const pageMode = ['agents', 'clients', 'groups'] as const;
type PageMode = typeof pageMode[number];

type GenericGroupGoalsDisplay = GroupGoalsDisplay & {
	Mm?: number;
	AgentId?: string;
	GroupId?: string;
};

@Component({
	selector: 'app-admin-goals-report',
	templateUrl: './admin-goals-report.component.html',
	styleUrls: ['./admin-goals-report.component.scss'],
})
export class AdminGoalsReportComponent implements OnInit, OnDestroy {
	_ss = inject(StoreService);
	router = inject(Router);
	fb = inject(FormBuilder);
	acr = inject(ActivatedRoute);
	private _util = inject(UtilService);
	constructor() {}

	goalsArrayDisplay;

	pageMode$ = new BehaviorSubject<PageMode>('groups');
	title$ = this.pageMode$.pipe(
		map(mode => {
			switch (mode) {
				case 'agents':
					return '';
				case 'clients':
					return '';
				case 'groups':
					return 'ביצועי מחלקות';
			}
		}),
	);

	sumOrQuantityControl = new FormControl(<'sum' | 'quantity'>'sum');
	sumOrQuantityFilter$ = this.sumOrQuantityControl.valueChanges.pipe(startWith('sum'));
	canChangeSumOrQuantity$ = new BehaviorSubject<boolean>(false);

	sortBy$ = new BehaviorSubject<{ key: keyof GenericGroupGoalsDisplay | null; order: 'asc' | 'desc' }>({ key: null, order: 'asc' });

	goalsGroup$ = new BehaviorSubject<GroupGoalsWithQuantity[]>([]);
	selectedGroup$ = new BehaviorSubject<{ id: string; text: string } | null>(null);
	selectedMonth$ = new BehaviorSubject<number | null>(null);
	displayValuesGroup$ = combineLatest([this.goalsGroup$, this.selectedGroup$, this.selectedMonth$, this.sortBy$, this.sumOrQuantityFilter$]).pipe(
		map(([goals, group, month, sortBy, filter]) => [goals.filter(goal => goal.Yy === this.year), group, month, sortBy, filter] as const),
		map(([goals, group, month, sortBy, filter]) => {
			if (!group) return [goals, 'GroupId', sortBy, filter] as const;
			if (month == null) return [goals.filter(goal => goal.GroupId === group.id), 'Mm', sortBy, filter] as const;
			return [goals.filter(goal => goal.GroupId === group.id && goal.Mm === month), 'AgentId', sortBy, filter] as const;
		}),
		map(([goals, key, sortBy, filter]): GroupGoalsDisplay[] => {
			const values = Object.values(
				goals.reduce((prev, curr) => {
					const keyToUse = curr[key];
					const totalKey = filter === 'sum' ? 'Tot' : 'QTot';
					const lastYearTotalKey = filter === 'sum' ? 'Ltot' : 'QLtot';
					const currentYearGoalKey = filter === 'sum' ? 'GYaad2' : 'QYaad2';
					const softCurrentYearGoalKey = filter === 'sum' ? 'GYaad' : 'QYaad';

					const agent = prev[keyToUse];
					if (agent) {
						agent.currentYearGoal += curr[currentYearGoalKey] || curr[softCurrentYearGoalKey] || 0;
						agent.total += curr[totalKey] || 0;
						agent.lastYearTotal += curr[lastYearTotalKey] || 0;
					} else {
						prev[keyToUse] = {
							...curr,
							[key]: keyToUse,
							currentYearGoal: curr[currentYearGoalKey] || curr[softCurrentYearGoalKey] || 0,
							total: curr[totalKey] || 0,
							lastYearTotal: curr[lastYearTotalKey] || 0,
							advancementPercentage: 0,
							growthPercentage: 0,
							previousYearGoal: 0,
							Mm: curr.Mm,
						};
					}
					return prev;
				}, {} as Record<string, GenericGroupGoalsDisplay>),
			);
			if (!sortBy.key || !values?.[0]) return values;

			// TODO Implement sorting
			// const byType = typeof values[0][sortBy.key];

			// switch (byType) {
			// 	case 'string':
			// 		return values.sort((a, b) => (a[sortBy.key] as string).localeCompare(b[sortBy.key] as string));
			// 	case 'number':
			// 		return values.sort((a, b) => (a[sortBy.key] as number) - (b[sortBy.key] as number));
			// 	default:
			// 		return values;
			// }
			return values;
		}),
		map(arr =>
			arr.map(obj => {
				if (obj.total && obj.currentYearGoal) {
					obj.advancementPercentage = (obj.total / obj.currentYearGoal) * 100;
				}
				if (obj.total && obj.lastYearTotal) {
					obj.growthPercentage = (obj.total / obj.lastYearTotal) * 100;
				}
				return obj;
			}),
		),
	);

	columnsThatHaveAtLeastOneValue$ = this.displayValuesGroup$.pipe(
		map(displayValues => {
			const columns = displayValues.reduce((prev, curr) => {
				Object.keys(curr).forEach((key: keyof GenericGroupGoalsDisplay) => {
					// if (key === 'AgentId' || key === 'GroupId' || key === 'Mm') return;
					if (curr[key]) prev.add(key);
				});
				return prev;
			}, new Set<keyof GenericGroupGoalsDisplay>());

			return Array.from(columns);
		}),
	);

	tableHeaders$ = combineLatest([this.pageMode$, this.selectedGroup$, this.selectedMonth$]).pipe(
		map(([mode, group, month]) => {
			switch (mode) {
				case 'agents':
					// TODO - add headers for agents
					return [];
				case 'clients':
					// TODO - add headers for clients
					return [];
				case 'groups':
					const baseHeaders = [
						{ key: 'currentYearGoal', text: 'יעד' },
						{ key: 'total', text: 'מכירות השנה' },
						{ key: 'advancementPercentage', text: 'אחוז התקדמות' },
						{ key: 'lastYearTotal', text: 'מכירות אשתקד' },
						{ key: 'growthPercentage', text: 'אחוז שינוי' },
					];
					if (!group) return [{ key: 'GroupName', text: 'מחלקה' }, ...baseHeaders];
					if (month == null) return [{ key: 'Mm', text: 'חודש' }, ...baseHeaders];
					return [{ key: 'AgentName', text: 'סוכן' }, ...baseHeaders];

				default:
					return [];
			}
		}),
		withLatestFrom(this.columnsThatHaveAtLeastOneValue$),
		map(([headers, columns]: [Array<{ key: keyof GenericGroupGoalsDisplay; text: string }>, Array<keyof GenericGroupGoalsDisplay>]) => headers.filter(header => columns.includes(header.key))),
	);

	tableData$ = combineLatest([this.pageMode$, this.selectedGroup$, this.selectedMonth$]).pipe(
		map(([mode, group, month]): Array<{ key: keyof GenericGroupGoalsDisplay; format: 'none' | 'month' | 'number' }> => {
			switch (mode) {
				case 'agents':
					// TODO - add data for agents
					return [];
				case 'clients':
					// TODO - add data for clients
					return [];
				case 'groups':
					const baseData: Array<{ key: keyof GenericGroupGoalsDisplay; format: 'none' | 'month' | 'number' }> = [
						{ key: 'currentYearGoal', format: 'number' },
						{ key: 'total', format: 'number' },
						{ key: 'advancementPercentage', format: 'number' },
						{ key: 'lastYearTotal', format: 'number' },
						{ key: 'growthPercentage', format: 'number' },
					];
					if (!group) {
						return [{ key: 'GroupName', format: 'none' }, ...baseData];
					}
					if (month == null) {
						return [{ key: 'Mm', format: 'month' }, ...baseData];
					}
					return [{ key: 'AgentName', format: 'none' }, ...baseData];
			}
		}),
		withLatestFrom(this.tableHeaders$),
		map(([data, headers]) => data.filter(d => headers.map(h => h.key).includes(d.key))),
	);

	year = new Date().getFullYear();
	shouldShowEditPopup = false;
	windowWidth;
	resizeObservable$: Observable<Event>;
	resizeSubscription$: Subscription = new Subscription();

	isSendingInfo = false;
	infoSendingMessage = 'מעדכן';

	agentsToGet = [];

	isAgents$ = this.pageMode$.pipe(map(mode => mode === 'agents'));
	isClients$ = this.pageMode$.pipe(map(mode => mode === 'clients'));
	isGroups$ = this.pageMode$.pipe(map(mode => mode === 'groups'));
	isAdmin = location.pathname.includes('admin');

	Months = monthsHeb;

	Loading = true;

	searchControl = new FormControl<string>('', { nonNullable: true });

	destroy$ = new Subject<void>();

	percentageInputControl = new FormControl<number>(null, [Validators.min(1), Validators.max(99)]);
	yearlyInputControl = new FormControl<number>(null, [Validators.min(12)]);

	@ViewChild(CdkVirtualScrollViewport) virtualScroll: CdkVirtualScrollViewport;

	main = true;

	hasMultipleCompanies = false;

	form = this.fb.group({
		company: [1],
	});

	companies: { CompanyName: string; Company: number }[] = [];
	goalsPerCompany$ = new BehaviorSubject<{ Company: number; Goals: any[] }[]>([]);

	ngOnInit(): void {
		this.pageMode$.next(this.acr.snapshot.queryParamMap.get('type') as typeof pageMode[number]);

		if (this._ss.CustomerInformation === null) {
			const CI = localStorage.getItem('CustomerInformation');
			if (CI === null) {
				alert('קרתה שגיאה בקריאת נתונים');
				history.back();
				return;
			}

			this._ss.CustomerInformation = JSON.parse(decompress(localStorage.getItem('CustomerInformation')));
		}

		// if (this._ss.CustomerInformation.HasMultipleCompanies > 0 && this.isAgents) {
		// 	this.hasMultipleCompanies = true;
		// }

		// if (!this.hasMultipleCompanies && environment.development) {
		// 	this.hasMultipleCompanies = true;
		// 	FormattedLog.log({ area: 'admin-goals', message: 'hasMultipleCompanies is true because of development environment' });
		// }

		const [username, agentid] = ['UserName', 'AgentId'].map(key => localStorage.getItem(key));

		if (username === null || agentid === null) {
			this.router.navigate(['login']);
			return;
		}

		this._ss.UserNameConnected = username;
		this._ss.AgentIdConnected = agentid;

		this.windowWidth = window.innerWidth;
		this.resizeObservable$ = fromEvent(window, 'resize');
		this.resizeSubscription$ = this.resizeObservable$.pipe(takeUntil(this.destroy$)).subscribe((evt: UIEvent) => {
			if (evt.currentTarget instanceof Window) {
				this.windowWidth = evt.currentTarget.innerWidth;
			}
		});

		if (!this.isAdmin || this._ss.CustomerInformation.Submanagers !== '1') {
			this.getGoalsAndUpdate();
			return;
		}

		if (this._ss.agentsOfSubmanager.length) {
			this.agentsToGet = this._ss.agentsOfSubmanager;
			this.getGoalsAndUpdate();
			return;
		}

		this._ss.getAdminAgents().subscribe({
			next: res => {
				if (typeof res == 'string') {
					alert('חלה תקלה בהבאת סוכנים עבור מנהל זה. נסו שנית');
					return;
				}

				this.agentsToGet = res.recordset.map(obj => obj.Agent);
				this.agentsToGet.push(this._ss.AgentIdConnected);

				this.getGoalsAndUpdate();
			},
			error: err => {
				alert('שגיאת שרת');
			},
		});
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	handleArrowPress() {
		if (this.selectedMonth$.value) {
			this.selectedMonth$.next(null);
			return;
		}
		if (this.selectedGroup$.value) {
			this.selectedGroup$.next(null);
			return;
		}

		history.back();
	}

	handleEditButton(identifier: string) {}

	handleEditButtonGroup(e: Event, agent: string, group: string) {}

	handleEditAgent() {}

	handleEditGroup() {}

	handleClientEdit() {}

	async handleGoalsSubmit() {}

	async handleGroupGoalsSubmit() {}

	async getGoalsAndUpdate() {
		switch (this.pageMode$.value) {
			case 'agents':
				// TODO - get agents goals
				break;
			case 'clients':
				// TODO - get clients goals
				break;
			case 'groups':
				this.getGroupsGoals();
				break;

			default:
				break;
		}
	}

	async handleGetCompanies() {
		if (!this.hasMultipleCompanies) return;
		return await new Promise<void>((resolve, reject) => {
			this._ss.GetCompaniesWithoutAgent().subscribe({
				next: res => {
					if (typeof res === 'string') {
						alert('חלה תקלה בהבאת חברות. נסו שנית');
						return;
					}

					this.companies = res.recordset;

					this.form.controls.company.setValue(res.recordset[0].Company);

					resolve();
				},
				error: err => {
					if (err.status !== 404) {
						alert('שגיאת שרת');
						return;
					}
					console.log('couldnt get all companies, trying to get companies of agent');
					this._ss.GetComppanys().subscribe({
						next: res => {
							if (typeof res === 'string') {
								alert('חלה תקלה בהבאת חברות. נסו שנית');
								return;
							}

							this.companies = res.recordset;

							this.form.controls.company.setValue(res.recordset[0]?.Company || 1);

							resolve();
						},
						error: err => {
							alert('שגיאת שרת');
						},
					});
				},
			});
		});
	}

	getAgentGoals(cm?: number) {
		this._ss.getAgentsGoals(this.agentsToGet, cm).subscribe({
			next: res => {
				if (typeof res === 'string') {
					alert('לא ניתן להביא נתונים. נסו שנית מאוחר יותר.');
					return history.back();
				}
				// this.goals = res.recordset;
				// this.goals$.next(this.goals);
				const arr = this.getAgentsGoalsFormatted();

				this.Loading = false;
				// this.goalsAgent$.next(arr);
			},
			error: err => {
				alert('שגיאת תקשורת, אנא נסה שנית מאוחר יותר.');
				history.back();
			},
		});
	}

	getGroupsGoals() {
		// if (this._ss.isLocalhost) {
		// 	this.goals = dummyGoals;
		// 	this.goals$.next(this.goals);
		// 	const arr = this.getGroupsGoalsFormatted();

		// 	this.Loading = false;
		// 	this.goalsGroup$.next(arr);
		// 	return;
		// }
		const callbacks = {
			next: res => {
				if (typeof res === 'string') {
					alert('חלה תקלה בהבאת סוכנים עבור מנהל זה. נסו שנית');
					return history.back();
				}

				this.goalsGroup$.next(res.recordset);

				if (res.recordset.length) {
					const firstGoal = res.recordset[0] as GroupGoalsWithQuantity;
					this.canChangeSumOrQuantity$.next('QTot' in firstGoal || 'QYaad' in firstGoal || 'Qyaad2' in firstGoal || 'QLtot' in firstGoal);
				}

				this.Loading = false;
			},
			error: err => {
				alert('שגיאת תקשורת, אנא נסה שנית מאוחר יותר.');
				history.back();
			},
		};
		if (!this.isAdmin) {
			this._ss.getGoalsGroups(this._ss.AgentIdConnected).subscribe(callbacks);
			return;
		}
		if (!this.agentsToGet.length) {
			this._ss.getGoalsGroups().subscribe(callbacks);
			return;
		}

		this._ss.getGoalsGroupsCustomAgents(this.agentsToGet).subscribe(callbacks);
	}

	getAgentsGoalsFormatted() {
		// const goals = this.goals
		// 	.reduce((prev, curr) => {
		// 		const agent = prev.find(arr => arr?.AgentCode == curr.AgentCode && arr?.GroupId == curr.GroupId && arr.cm === curr.cm);
		// 		if (agent) {
		// 			agent.currentYearGoal += curr.Year == this.year ? curr.Yaad2 || curr.Yaad : null;
		// 			agent.previousYearGoal += curr.Year == this.year ? null : curr.Yaad2 || curr.Yaad;
		// 			agent.total += curr.Tot || 0;
		// 			agent.lastYearTotal += curr.Ltot || 0;
		// 		} else {
		// 			prev.push({
		// 				AgentCode: curr.AgentCode,
		// 				AgentName: curr.AgentName,
		// 				currentYearGoal: curr.Year == this.year ? curr.Yaad2 || curr.Yaad : 0,
		// 				previousYearGoal: curr.Year == this.year ? 0 : curr.Yaad2 || curr.Yaad,
		// 				total: curr.Tot || 0,
		// 				lastYearTotal: curr.Ltot || 0,
		// 				cm: curr.cm,
		// 			});
		// 		}
		// 		return prev;
		// 	}, [])
		// 	.map(gl => {
		// 		if (gl.total && gl.currentYearGoal) {
		// 			gl.advancementPercentage = (gl.total / gl.currentYearGoal) * 100;
		// 		}
		// 		if (gl.total && gl.lastYearTotal) {
		// 			gl.growthPercentage = (gl.total / gl.lastYearTotal) * 100;
		// 		}
		// 		return gl;
		// 	});
		// return goals;
	}

	getGroupsGoalsFormatted() {
		// return (this.goals as GroupGoals[])
		// 	.reduce((prev, curr) => {
		// 		const agent = prev.find(arr => arr?.AgentId == curr.AgentId && arr?.GroupId == curr.GroupId);
		// 		if (agent) {
		// 			agent.currentYearGoal += curr.GYaad2 || curr.GYaad;
		// 			agent.previousYearGoal += curr.GYaad2 || curr.GYaad;
		// 			agent.total += curr.Tot || 0;
		// 			agent.lastYearTotal += curr.Ltot || 0;
		// 		} else {
		// 			prev.push({
		// 				AgentId: curr.AgentId,
		// 				AgentName: curr.AgentName,
		// 				GroupId: curr.GroupId,
		// 				GroupName: curr.GroupName,
		// 				currentYearGoal: curr.GYaad2 || curr.GYaad,
		// 				previousYearGoal: curr.GYaad2 || curr.GYaad,
		// 				total: curr.Tot || 0,
		// 				lastYearTotal: curr.Ltot || 0,
		// 			});
		// 		}
		// 		return prev;
		// 	}, [])
		// 	.map(gl => {
		// 		if (gl.total && gl.currentYearGoal) {
		// 			gl.advancementPercentage = (gl.total / gl.currentYearGoal) * 100;
		// 		}
		// 		if (gl.total && gl.lastYearTotal) {
		// 			gl.growthPercentage = (gl.total / gl.lastYearTotal) * 100;
		// 		}
		// 		return gl;
		// 	});
	}

	getClientsGoals() {
		this._ss.getGoalsClients().subscribe({
			next: res => {
				if (res === 'ConnectionError' || res === 'RequestError') {
					alert('ישנה תקלת תקשורת, אנא נסו שנית מאוחר יותר');
					return history.back();
				}

				// this.goals = res.recordset;
				// this.goals$.next(this.goals);

				// this.goalsClientFormatted = Object.values(
				// 	res.recordset.reduce((prev: { [key: string]: ClientGoalsDisplay }, curr) => {
				// 		const { Cl, Yy1, Cn, Tot, Ltot, Yaad1, Yaad2 } = curr;

				// 		const client = prev[Cl];
				// 		if (client) {
				// 			client.currentYearGoal += Yy1 == this.year ? +Yaad2 || +Yaad1 : 0;
				// 			client.previousYearGoal += Yy1 == this.year ? 0 : +Yaad2 || +Yaad1;
				// 			client.total += Tot || 0;
				// 			client.lastYearTotal += Ltot || 0;
				// 			return prev;
				// 		}

				// 		prev[Cl] = {
				// 			Cl,
				// 			Cn,
				// 			total: Tot,
				// 			lastYearTotal: Ltot,
				// 			currentYearGoal: Yy1 == this.year ? +Yaad2 || +Yaad1 : 0,
				// 			previousYearGoal: Yy1 == this.year ? 0 : +Yaad2 || +Yaad1,
				// 			advancementPercentage: 0,
				// 			growthPercentage: 0,
				// 		};

				// 		return prev;
				// 	}, {}),
				// ).map((gl: ClientGoalsDisplay) => {
				// 	const { total, currentYearGoal, lastYearTotal } = gl;
				// 	if (total && currentYearGoal) {
				// 		gl.advancementPercentage = (total / currentYearGoal) * 100;
				// 	}

				// 	if (total && lastYearTotal) {
				// 		gl.growthPercentage = (total / lastYearTotal) * 100;
				// 	}

				// 	return gl;
				// });

				// console.log(this.goalsClientFormatted);
				// this.Loading = false;
				// this.goalsClient$.next(this.goalsClientFormatted);
			},
		});
	}

	search() {
		const page = this.pageMode$.value;
		// if (['clients', 'groups'].includes(page)) this.virtualScroll.scrollToIndex(0);
		// if (this.isClients) this.virtualScroll.scrollToIndex(0);

		const value = this.searchControl.value?.toLowerCase?.();

		// if (!value) {
		// 	if (page === 'clients') {
		// 		this.goalsClient$.next(this.goalsClientFormatted);
		// 		return;
		// 	}
		// 	if (page === 'groups') {
		// 		this.goalsGroup$.next(this.getGroupsGoalsFormatted());
		// 		return;
		// 	}

		// 	this.goalsAgent$.next(this.getAgentsGoalsFormatted());
		// } else {
		// 	const pattern = () => new RegExp(value.replace(/[\/*\+\-\?,]/g, ''), 'i');

		// 	if (page === 'clients') {
		// 		this.goalsClient$.next(this.goalsClientFormatted.filter(client => pattern().test(client.Cn || '') || pattern().test(client.Cl || '')));
		// 		return;
		// 	}

		// 	if (page === 'groups') {
		// 		this.goalsGroup$.next(this.getGroupsGoalsFormatted().filter(group => pattern().test(group.GroupName || '') || pattern().test(group.AgentName || '')));
		// 		return;
		// 	}

		// 	this.goalsAgent$.next(this.getAgentsGoalsFormatted().filter(agent => pattern().test(agent.AgentName || '')));
		// }
	}

	handleRowClick(row: GenericGroupGoalsDisplay) {
		if (this.selectedMonth$.value) return null;
		if (this.selectedGroup$.value) {
			this.selectedMonth$.next(row.Mm);
			return;
		}
		this.selectedGroup$.next({ id: row.GroupId, text: row.GroupName });
	}

	SearchClear() {
		this.searchControl.reset();
		this.search();
	}

	affectAllInputs(by: 'percentage' | 'yearly') {}

	closeEditPopup() {
		this.shouldShowEditPopup = this.isSendingInfo = false;

		document.body.classList.remove('no-scroll');

		this.yearlyInputControl.reset();
		this.percentageInputControl.reset();
	}

	setupListeners() {}

	translateForSortAgent(value: typeof agentsValues[number]) {
		this.translateForSort({ value, screen: 'isAgents' });
	}

	translateForSortClient(value: typeof clientsValues[number]) {
		this.translateForSort({ value, screen: 'isClients' });
	}

	translateForSortGroup(value: typeof groupsValues[number]) {
		this.translateForSort({ value, screen: 'isGroups' });
	}

	translateForSort(
		payload: { value: typeof agentsValues[number]; screen: 'isAgents' } | { value: typeof clientsValues[number]; screen: 'isClients' } | { value: typeof groupsValues[number]; screen: 'isGroups' },
	) {
		let i: number, arrayToSort: any[];
		const { value, screen } = payload;

		switch (screen) {
			case 'isAgents':
				i = agentsValues.findIndex(arr => arr === value);
				// 	arrayToSort = this.goalsAgent$.value;
				// 	break;
				// case 'isClients':
				// 	i = clientsValues.findIndex(arr => arr === value);
				// 	arrayToSort = this.goalsClient$.value;
				break;
			case 'isGroups':
				i = groupsValues.findIndex(arr => arr === value);
				arrayToSort = this.goalsGroup$.value;
				break;

			default:
				break;
		}

		if (i === undefined || i === -1) {
			console.log('i should never show');
			return;
		}

		this._util.sortWithIndex(value, i, arrayToSort);

		switch (screen) {
			case 'isAgents':
				// 	this.goalsAgent$.next([...arrayToSort]);
				// 	break;
				// case 'isClients':
				// 	this.goalsClient$.next([...arrayToSort]);
				break;
			case 'isGroups':
				this.goalsGroup$.next([...arrayToSort]);
				break;

			default:
				break;
		}
	}

	downloadExcel() {
		const dataObject: ExcelObject = { data: [], filename: '', extension: 'xlsx' };

		// const table = document.querySelector(this.tableQuerySelector || 'table');
		const title = document.querySelector('.header__title');

		if (title) {
			dataObject.filename = title.textContent;
			dataObject.filename = this._util.removeProblematicCharacters(dataObject.filename);
		}

		const wb = utils.book_new();
		// const dataToAddToWb = this.goals$.value;

		// const sheet = utils.json_to_sheet(dataToAddToWb);

		// utils.book_append_sheet(wb, sheet, 'Goals');

		if (!wb.Workbook) wb.Workbook = {};
		if (!wb.Workbook.Views) wb.Workbook.Views = [{}];
		wb.Workbook.Views[0].RTL = true;

		writeFile(wb, `${dataObject.filename}.${dataObject.extension}`, { bookType: dataObject.extension, compression: true, cellStyles: true });
		return;
	}
}
